import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNowStrict, isToday, isTomorrow, isValid, parseISO } from 'date-fns';

/** Merge Tailwind classes so later conditional classes actually win. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(date) ? date : null;
}

export function formatDate(value: string | number | Date | null | undefined, pattern = 'd MMM yyyy'): string {
  const date = toDate(value);
  return date ? format(date, pattern) : '—';
}

export function formatDateTime(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, "d MMM yyyy 'at' HH:mm") : '—';
}

/** "in 3 days" / "2 hours ago" — with today and tomorrow spelled out. */
export function formatRelative(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  if (isToday(date)) return `Today, ${format(date, 'HH:mm')}`;
  if (isTomorrow(date)) return `Tomorrow, ${format(date, 'HH:mm')}`;

  const distance = formatDistanceToNowStrict(date);
  return date.getTime() > Date.now() ? `in ${distance}` : `${distance} ago`;
}

export function formatSalary(
  min: number | null | undefined,
  max: number | null | undefined,
  currency = 'USD'
): string {
  if (min == null && max == null) return 'Not disclosed';

  const compact = (n: number) =>
    new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 0, notation: 'compact' }).format(n);

  if (min != null && max != null) return `${compact(min)} – ${compact(max)}`;
  return compact((min ?? max) as number);
}

export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function initials(name: string | null | undefined): string {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase();
}

/** Deterministic accent per person, so an avatar keeps the same colour everywhere. */
export function avatarTone(seed: string): string {
  const tones = [
    'bg-brand-soft text-brand',
    'bg-info-soft text-info',
    'bg-positive-soft text-positive',
    'bg-warning-soft text-warning',
    'bg-danger-soft text-danger',
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return tones[hash % tones.length] as string;
}

export function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1).trimEnd()}…`;
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Very small markdown renderer for the bullet lists and bold runs the AI produces.
 * Escapes first, so model output can never inject markup.
 */
export function renderRichText(markdown: string | null | undefined): string {
  if (!markdown) return '';

  const escaped = markdown
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const lines = escaped.split('\n');
  const html: string[] = [];
  let inList = false;

  const closeList = () => {
    if (inList) {
      html.push('</ul>');
      inList = false;
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      closeList();
      continue;
    }
    if (/^[-*]\s+/.test(trimmed)) {
      if (!inList) {
        html.push('<ul>');
        inList = true;
      }
      html.push(`<li>${inline(trimmed.replace(/^[-*]\s+/, ''))}</li>`);
      continue;
    }
    if (/^#{1,3}\s+/.test(trimmed)) {
      closeList();
      const level = Math.min(trimmed.match(/^#+/)![0].length + 1, 4);
      html.push(`<h${level}>${inline(trimmed.replace(/^#+\s+/, ''))}</h${level}>`);
      continue;
    }
    closeList();
    html.push(`<p>${inline(trimmed)}</p>`);
  }
  closeList();

  return html.join('');
}

function inline(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/(?<!\*)\*(?!\*)(.+?)\*/g, '<em>$1</em>');
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
